/**
 * Mobile Module for Workspace
 * Manages mobile-specific UI: FABs, bottom sheets, toolbar, gestures
 * Adapted from designer's mobile module to the 3-column shell layout.
 *
 * Workspace layout: rail (sidebar) | stage (center) | right-panel
 * On mobile: rail collapses to bottom tab bar, right-panel becomes a sheet,
 * stage takes full width. Design mode gets canvas FABs and bottom toolbar.
 *
 * Module entry — lifecycle (init/destroy), FABs, event wiring, window global.
 * Sibling responsibilities: state.js (shared mutable state), icons.js (SVG
 * set), sheets.js (bottom sheets + rendering), toolbar.js (bottom toolbar).
 */

import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { createLogger } from '../../../utils/logger.js'
import { isMobile, isTablet } from '../../../utils/viewport.js'
import { state } from './state.js'
import { ICONS } from './icons.js'
import { createSheetOverlay, toggleSheet, openSheet, closeSheet, renderPreviewContent } from './sheets.js'
import { createDesignToolbar, toggleSecondary, initKeyboardHandler } from './toolbar.js'

const log = createLogger('mobile')

// isMobile / isTablet imported from utils/viewport.js (single source of truth
// aligned with mobile.css breakpoints). Re-exported below for default-export
// API compatibility.
export { isMobile, isTablet }

/**
 * Initialize mobile module
 * Called when entering design mode on mobile
 */
export function init() {
  if (state.initialized) return
  if (!isMobile()) return

  log.info('Mobile module initializing...')

  document.body.classList.add('ws-mobile')

  createDesignFABs()
  createSheetOverlay()
  createDesignToolbar()
  initGestures()
  listenEvents()

  state.initialized = true
  log.info('Mobile module ready')
}

/**
 * Create FAB group for design mode (overlays the canvas area)
 */
function createDesignFABs() {
  const stage = document.querySelector('.stage-body')
  if (!stage) return

  // Remove existing
  const existing = stage.querySelector('.ws-mobile-fab-group')
  if (existing) existing.remove()

  const group = document.createElement('div')
  group.className = 'ws-mobile-fab-group'

  const fabs = [
    { id: 'preview', icon: ICONS.eye, label: 'Preview' },
    { id: 'arts', icon: ICONS.image, label: 'Arts' },
    { id: 'products', icon: ICONS.package, label: 'Products' },
  ]

  fabs.forEach(({ id, icon }) => {
    const btn = document.createElement('button')
    btn.className = 'ws-mobile-fab-btn'
    btn.dataset.sheet = id
    btn.innerHTML = icon
    btn.onclick = () => toggleSheet(id)
    group.appendChild(btn)
  })

  stage.appendChild(group)
}

/**
 * Gestures — sheet drag and keyboard handling
 * (sheet drag is bound per-sheet inside sheets.js createSheet)
 */
function initGestures() {
  initKeyboardHandler()
}

/**
 * Event listeners
 */
function listenEvents() {
  eventBus.on(EVENTS.SDK_READY, () => {
    // Refresh preview if sheet is open
    if (state.activeSheet === 'preview') {
      setTimeout(renderPreviewContent, 300)
    }
  })

  eventBus.on(EVENTS.PRODUCT_CHANGE, () => {
    if (state.activeSheet === 'preview') {
      setTimeout(renderPreviewContent, 300)
    }
  })
}

/**
 * Cleanup — remove all mobile DOM elements
 */
export function destroy() {
  const selectors = [
    '.ws-mobile-fab-group',
    '#wsMobileSheetOverlay',
    '#wsMobileSheet-preview',
    '#wsMobileSheet-arts',
    '#wsMobileSheet-products',
    '#wsMobileToolbar',
  ]

  selectors.forEach(sel => {
    const el = document.querySelector(sel)
    if (el) el.remove()
  })

  document.body.classList.remove('ws-mobile')
  state.initialized = false
  state.activeSheet = null
  log.info('Mobile module destroyed')
}

// Global API
window.WorkspaceMobile = {
  openSheet,
  closeSheet,
  toggleSheet,
  toggleSecondary,
}

export default { init, destroy, isMobile, isTablet }
